import Constants from "expo-constants";
import { Linking, Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { useAppTheme } from "@/theme/app-theme";

/**
 * The two faces of the store-update gate (ADR-028's design, inside ADR-029).
 *
 * `UpdateRequiredScreen` replaces the app: the server said this build is
 * retired, and the only way forward is the store. It is only ever reachable
 * after a successful, validated server answer — never from an error path —
 * so it cannot strand a user because a request failed.
 *
 * `UpdateBanner` is the dismissible "recommended" prompt. Dismissal is
 * remembered per installed build (a prompt, not a nag).
 */

function openStore(): void {
  if (Platform.OS === "android") {
    const pkg = Constants.expoConfig?.android?.package ?? "com.opncue.app";
    void Linking.openURL(`market://details?id=${pkg}`).catch(() => {
      void Linking.openURL(
        `https://play.google.com/store/apps/details?id=${pkg}`,
      );
    });
    return;
  }
  // iOS deep links need the numeric App Store ID, which does not exist until
  // the listing does. Until then the button opens the App Store app itself;
  // the ID slots in here the day App Store Connect issues one.
  void Linking.openURL("itms-apps://apps.apple.com/");
}

export function UpdateRequiredScreen({
  onLayout,
}: {
  onLayout?: () => void;
}) {
  const { colors } = useAppTheme();
  // `useTranslation`, not the `i18n.t` this file could have used: the banner
  // below outlives a language change, and a bare `t` call would keep the old
  // language until something else re-rendered it. Safe here because the whole
  // tree is mounted only once i18n has resolved — `RootLayout` renders
  // `RootLayoutContent` behind an `i18nReady` gate, and this screen is inside
  // it.
  const { t } = useTranslation();

  return (
    <View
      onLayout={onLayout}
      style={[styles.screen, { backgroundColor: colors.background }]}>
      <Text style={[styles.title, { color: colors.text }]}>
        {t("update.requiredTitle")}
      </Text>
      <Text style={[styles.body, { color: colors.textMuted }]}>
        {t("update.requiredBody")}
      </Text>
      <Pressable
        accessibilityRole="button"
        onPress={openStore}
        style={[styles.button, { backgroundColor: colors.primary }]}>
        <Text style={styles.buttonLabel}>
          {Platform.OS === "android"
            ? t("update.openPlayStore")
            : t("update.openAppStore")}
        </Text>
      </Pressable>
    </View>
  );
}

export function UpdateBanner({ onDismiss }: { onDismiss: () => void }) {
  const { colors } = useAppTheme();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();

  return (
    <View
      style={[
        styles.banner,
        {
          top: insets.top + 8,
          backgroundColor: colors.surface,
          borderColor: colors.border,
        },
      ]}>
      <View style={styles.bannerText}>
        <Text style={[styles.bannerTitle, { color: colors.text }]}>
          {t("update.availableTitle")}
        </Text>
        <Text style={[styles.bannerBody, { color: colors.textMuted }]}>
          {t("update.availableBody")}
        </Text>
      </View>
      <Pressable
        accessibilityRole="button"
        onPress={openStore}
        style={[styles.bannerAction, { backgroundColor: colors.primary }]}>
        <Text style={styles.buttonLabel}>{t("update.action")}</Text>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("update.dismissA11y")}
        onPress={onDismiss}
        hitSlop={8}
        style={styles.bannerDismiss}>
        <Text style={[styles.bannerDismissLabel, { color: colors.text }]}>
          ✕
        </Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    textAlign: "center",
  },
  body: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: "center",
  },
  button: {
    marginTop: 12,
    borderRadius: 12,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  buttonLabel: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "600",
  },
  banner: {
    position: "absolute",
    left: 12,
    right: 12,
    zIndex: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 12,
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 4,
  },
  bannerText: {
    flex: 1,
    gap: 2,
  },
  bannerTitle: {
    fontSize: 14,
    fontWeight: "700",
  },
  bannerBody: {
    fontSize: 13,
    lineHeight: 17,
  },
  bannerAction: {
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  bannerDismiss: {
    padding: 4,
  },
  bannerDismissLabel: {
    fontSize: 14,
    opacity: 0.6,
  },
});
